import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { FiBell, FiCheckCircle, FiExternalLink, FiPaperclip, FiPause, FiPlay, FiSearch, FiX, FiArrowLeft } from 'react-icons/fi'

import './ClientSupportManagement.css'
import TeamHeader from '../TeamHeader/TeamHeader'

import ClientTicketChat, { ChatMeta, ticketChanged } from './ClientTicketChat'
import NotificationSettingsModal from './NotificationSettingsModal'
import { apiRequest, errorMessage, formatDate, formatDateTime, formatDuration, timeAgo } from './supportApi'
import {
    DepartmentOption, FnnUser, PRIORITIES, SlaInfo, StaffMeta, StaffTicket, WORKING_STATUSES,
} from './clientTicketTypes'

type View = 'inbox' | 'assigned' | 'department' | 'all'

const LIST_POLL_MS = 30000
const BASE = '/portal/api/client-tickets'

const STATUS_CLASS: Record<string, string> = {
    'Submitted': 'cs-s-submitted',
    'Investigating': 'cs-s-investigating',
    'In Progress': 'cs-s-progress',
    'Implementing': 'cs-s-implementing',
    'Resolved': 'cs-s-resolved',
}

const PRIORITY_CLASS: Record<string, string> = {
    Low: 'cs-p-low', Medium: 'cs-p-medium', High: 'cs-p-high', Critical: 'cs-p-critical', Emergency: 'cs-p-emergency',
}

/** FNN's priority once triaged; before that, the customer's request (dashed, so it reads as unconfirmed). */
function PriorityBadge({ ticket }: { ticket: StaffTicket }) {
    if (ticket.priority) return <span className={`cs-priority ${PRIORITY_CLASS[ticket.priority]}`}>{ticket.priority}</span>
    if (ticket.requested_priority) {
        return (
            <span className={`cs-priority cs-p-requested ${PRIORITY_CLASS[ticket.requested_priority]}`}
                title="Requested by the customer. Not triaged yet.">
                Requested: {ticket.requested_priority}
            </span>
        )
    }
    return <span className="cs-priority cs-p-none">Untriaged</span>
}

// ─── SLA helpers ──────────────────────────────────────────────────────────────

function useNow(intervalMs: number) {
    const [now, setNow] = useState(Date.now())
    useEffect(() => {
        const t = window.setInterval(() => setNow(Date.now()), intervalMs)
        return () => window.clearInterval(t)
    }, [intervalMs])
    return now
}

/**
 * Server values are a snapshot; move a running clock forward locally.
 * The clock only counts on business days, so it doesn't move on weekends/holidays.
 */
function liveSla(sla: SlaInfo, fetchedAt: number, now: number) {
    const drift = sla.state === 'running' && sla.counting_now ? Math.max(0, (now - fetchedAt) / 1000) : 0
    const remaining = sla.remaining_seconds - drift
    const elapsed = sla.elapsed_seconds + drift
    const target = sla.target_hours * 3600
    return { remaining, elapsed, target, used: Math.min(1, elapsed / target), breached: remaining < 0 }
}

function slaTone(sla: SlaInfo, live: ReturnType<typeof liveSla>) {
    if (sla.state === 'stopped') return 'done'
    if (live.breached) return 'late'
    if (sla.state === 'paused') return 'paused'
    if (live.remaining < live.target * 0.25) return 'soon'
    return 'ok'
}

function SlaChip({ sla, fetchedAt, now }: { sla: SlaInfo; fetchedAt: number; now: number }) {
    const live = liveSla(sla, fetchedAt, now)
    const tone = slaTone(sla, live)
    const text =
        sla.state === 'stopped' ? `Closed in ${formatDuration(live.elapsed)}`
            : live.breached ? `Overdue by ${formatDuration(-live.remaining)}`
                : sla.state === 'paused' ? `Paused with ${formatDuration(live.remaining)} left`
                    : `${formatDuration(live.remaining)} left${sla.counting_now ? '' : ' (not counting today)'}`
    return (
        <span className={`cs-sla-chip is-${tone}`}>
            {sla.state === 'paused' && <FiPause size={10} />}
            {text}
        </span>
    )
}

function SlaMeter({ sla, fetchedAt, now }: { sla: SlaInfo; fetchedAt: number; now: number }) {
    const live = liveSla(sla, fetchedAt, now)
    const tone = slaTone(sla, live)
    return (
        <span className={`cs-sla-meter is-${tone}`} aria-hidden="true">
            <span style={{ width: `${Math.round(live.used * 100)}%` }} />
        </span>
    )
}

// ─── Detail: SLA card ─────────────────────────────────────────────────────────

function SlaCard({ ticket, fetchedAt, now }: { ticket: StaffTicket; fetchedAt: number; now: number }) {
    const [showLog, setShowLog] = useState(false)
    const { sla } = ticket
    const live = liveSla(sla, fetchedAt, now)
    const tone = slaTone(sla, live)

    const headline =
        sla.state === 'stopped' ? 'Clock stopped'
            : live.breached ? 'Overdue'
                : sla.state === 'paused' ? 'Clock paused'
                    : sla.counting_now ? 'Clock running' : 'Clock not counting today'

    return (
        <section className={`cs-card cs-sla-card is-${tone}`}>
            <div className="cs-sla-top">
                <div>
                    <h3>{headline}</h3>
                    <p>
                        {sla.state === 'paused' && 'Waiting on the customer. Paused time doesn’t count toward the SLA.'}
                        {sla.state === 'running' && !live.breached && sla.due_at && `Due ${formatDateTime(sla.due_at)}`}
                        {sla.state === 'running' && !live.breached && !sla.counting_now && '. Weekends and holidays don’t count.'}
                        {sla.state === 'running' && live.breached && 'The SLA target has passed. Keep the customer updated.'}
                        {sla.state === 'stopped' && `Resolved ${formatDateTime(sla.stopped_at)}`}
                    </p>
                </div>
                <div className="cs-sla-figure">
                    <strong>{sla.state === 'stopped' ? formatDuration(live.elapsed) : formatDuration(Math.abs(live.remaining))}</strong>
                    <span>{sla.state === 'stopped' ? 'total SLA time' : live.breached ? 'over target' : 'remaining'}</span>
                </div>
            </div>
            <SlaMeter sla={sla} fetchedAt={fetchedAt} now={now} />
            <p className="cs-sla-foot">
                Target {sla.target_hours} business hours for {ticket.priority
                    ? `${ticket.priority} priority`
                    : ticket.requested_priority
                        ? `${ticket.requested_priority} priority (customer’s request, until triaged)`
                        : 'Medium priority (until triaged)'}.
                Used {formatDuration(live.elapsed)}. Weekends and holidays don’t count.
            </p>

            {ticket.sla_events && ticket.sla_events.length > 0 && (
                <>
                    <button className="cs-link" onClick={() => setShowLog(v => !v)}>
                        {showLog ? 'Hide clock history' : `Show clock history (${ticket.sla_events.length})`}
                    </button>
                    {showLog && (
                        <ol className="cs-sla-log">
                            {ticket.sla_events.map(e => (
                                <li key={e.id} className={`is-${e.event}`}>
                                    <span className="cs-sla-log-dot" />
                                    <div>
                                        <strong>{e.event_label}</strong>
                                        <span>{formatDateTime(e.at)}{e.actor_name ? ` by ${e.actor_name}` : ' (automatic)'}</span>
                                        {e.note && <em>{e.note}</em>}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </>
            )}
        </section>
    )
}

// ─── Detail: triage / assign / work ───────────────────────────────────────────

function TriageCard({ ticket, departments, onSaved }: {
    ticket: StaffTicket; departments: DepartmentOption[]; onSaved: (t: StaffTicket) => void
}) {
    // Untriaged tickets start from what the customer asked for
    const [priority, setPriority] = useState(ticket.priority ?? ticket.requested_priority ?? '')
    const [dept, setDept] = useState(ticket.target_department ? String(ticket.target_department) : '')
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        setPriority(ticket.priority ?? ticket.requested_priority ?? '')
        setDept(ticket.target_department ? String(ticket.target_department) : '')
    }, [ticket.ticket_id, ticket.priority, ticket.target_department])

    const changed = priority !== (ticket.priority ?? '') || dept !== (ticket.target_department ? String(ticket.target_department) : '')

    const save = async () => {
        if (!priority || !dept) { toast.error('Pick a priority and a team.'); return }
        setBusy(true)
        const { ok, data } = await apiRequest<StaffTicket>(`${BASE}/manage/${ticket.ticket_id}/`, {
            method: 'PATCH', json: { priority, target_department: Number(dept) },
        })
        setBusy(false)
        if (!ok) { toast.error(errorMessage(data, 'Triage not saved.')); return }
        toast.success(ticket.target_department ? 'Ticket updated.' : `Routed to ${data.target_department_name}.`)
        onSaved(data)
    }

    return (
        <section className="cs-card">
            <h3>{ticket.target_department ? 'Priority and team' : 'Triage'}</h3>
            {!ticket.target_department && <p className="cs-card-hint">Set the priority and send it to the team that should resolve it. Their leader is notified.</p>}
            {ticket.requested_priority && (
                <p className="cs-requested">
                    Customer requested <strong>{ticket.requested_priority}</strong>.
                    {priority && priority !== ticket.requested_priority && ' If you change it, they’ll see the new priority in the conversation.'}
                </p>
            )}
            <div className="cs-row-2">
                <label className="cs-field">
                    <span>Priority</span>
                    <select value={priority} onChange={e => setPriority(e.target.value)}>
                        <option value="">Select…</option>
                        {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                    </select>
                </label>
                <label className="cs-field">
                    <span>Team</span>
                    <select value={dept} onChange={e => setDept(e.target.value)}>
                        <option value="">Select…</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}{d.leader_name ? '' : ' (no leader)'}</option>)}
                    </select>
                </label>
            </div>
            <p className="cs-team-hint">
                Not sure which team?{' '}
                <a href="/support/team_directory" target="_blank" rel="noopener noreferrer">
                    See teams and their leaders <FiExternalLink size={11} aria-label="(opens in a new tab)" />
                </a>
            </p>
            {ticket.target_department && dept !== String(ticket.target_department) && (
                <p className="cs-warn">Moving it to another team clears the current assignee.</p>
            )}
            <div className="cs-card-actions">
                <button className="cs-btn cs-btn-primary" disabled={busy || !changed} onClick={save}>
                    {busy ? 'Saving…' : ticket.target_department ? 'Save changes' : 'Route ticket'}
                </button>
            </div>
        </section>
    )
}

function AssignCard({ ticket, fnnUsers, onSaved }: {
    ticket: StaffTicket; fnnUsers: FnnUser[]; onSaved: (t: StaffTicket) => void
}) {
    const [search, setSearch] = useState(ticket.assigned_to_name ?? '')
    const [selected, setSelected] = useState<number | null>(ticket.assigned_to)
    const [focused, setFocused] = useState(false)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        setSearch(ticket.assigned_to_name ?? '')
        setSelected(ticket.assigned_to)
    }, [ticket.ticket_id, ticket.assigned_to, ticket.assigned_to_name])

    const matches = useMemo(() => {
        const q = search.toLowerCase().trim()
        return fnnUsers.filter(u => !q || `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)).slice(0, 8)
    }, [search, fnnUsers])

    const save = async (assignTo: number | null) => {
        setBusy(true)
        const { ok, data } = await apiRequest<StaffTicket>(`${BASE}/manage/${ticket.ticket_id}/`, {
            method: 'PATCH', json: { assigned_to: assignTo },
        })
        setBusy(false)
        if (!ok) { toast.error(errorMessage(data, 'Assignment not saved.')); return }
        toast.success(assignTo ? `Assigned to ${data.assigned_to_name}.` : 'Assignee removed.')
        onSaved(data)
    }

    return (
        <section className="cs-card">
            <h3>Assignee</h3>
            {!ticket.assigned_to && <p className="cs-card-hint">Nobody is working on this yet.</p>}
            <div className="cs-autocomplete">
                <input
                    value={search}
                    placeholder="Search FNN people by name or email…"
                    onFocus={() => setFocused(true)}
                    onBlur={() => window.setTimeout(() => setFocused(false), 150)}
                    onChange={e => { setSearch(e.target.value); setSelected(null); setFocused(true) }}
                    aria-label="Assignee"
                />
                {focused && matches.length > 0 && (
                    <ul className="cs-autocomplete-list" role="listbox">
                        {matches.map(u => (
                            <li key={u.id} role="option" aria-selected={selected === u.id}
                                onMouseDown={e => { e.preventDefault(); setSelected(u.id); setSearch(`${u.first_name} ${u.last_name}`); setFocused(false) }}>
                                <strong>{u.first_name} {u.last_name}</strong>
                                <span>{u.email}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className="cs-card-actions">
                {ticket.assigned_to && (
                    <button className="cs-btn" disabled={busy} onClick={() => save(null)}>Remove assignee</button>
                )}
                <button className="cs-btn cs-btn-primary" disabled={busy || !selected || selected === ticket.assigned_to}
                    onClick={() => save(selected)}>
                    {busy ? 'Saving…' : 'Assign'}
                </button>
            </div>
        </section>
    )
}

type Compose = null | 'request_input' | 'request_validation' | 'verify' | 'resolve'

function WorkCard({ ticket, waitDays, reopenDays, onSaved }: {
    ticket: StaffTicket; waitDays: number; reopenDays: number; onSaved: (t: StaffTicket) => void
}) {
    const p = ticket.permissions
    const [status, setStatus] = useState(ticket.status)
    const [notes, setNotes] = useState(ticket.resolution_notes ?? '')
    const [compose, setCompose] = useState<Compose>(null)
    const [text, setText] = useState('')
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        setStatus(ticket.status)
        setNotes(ticket.resolution_notes ?? '')
    }, [ticket.ticket_id, ticket.status, ticket.resolution_notes])

    useEffect(() => { setCompose(null); setText('') }, [ticket.ticket_id])

    const patch = async (json: object, success: string) => {
        setBusy(true)
        const { ok, data } = await apiRequest<StaffTicket>(`${BASE}/manage/${ticket.ticket_id}/`, { method: 'PATCH', json })
        setBusy(false)
        if (!ok) { toast.error(errorMessage(data, 'Not saved.')); return }
        toast.success(success)
        onSaved(data)
    }

    const act = async (action: string, message: string, success: string) => {
        setBusy(true)
        const { ok, data } = await apiRequest<StaffTicket>(`${BASE}/${ticket.ticket_id}/actions/`, {
            method: 'POST', json: { action, message },
        })
        setBusy(false)
        if (!ok) { toast.error(errorMessage(data, 'That didn’t work. Try again.')); return }
        toast.success(success)
        setCompose(null)
        setText('')
        onSaved(data)
    }

    const waitNote = `If they don’t reply within ${waitDays} business days, the ticket closes automatically. They get two reminders before that.`

    const composeCopy: Record<Exclude<Compose, null>, {
        title: string; label: string; placeholder: string; button: string; success: string; required: boolean
    }> = {
        request_input: {
            title: 'Ask the customer for information',
            label: `Your question appears in the conversation and is emailed to the customer. The SLA clock pauses until they reply. ${waitNote}`,
            placeholder: 'What do you need from them? e.g. which browser, a screenshot, the time it happened',
            button: 'Send and pause clock',
            success: 'Question sent. The clock is paused.',
            required: true,
        },
        request_validation: {
            title: 'Ask the customer to test the fix',
            label: `The customer is asked to confirm. The SLA clock pauses until they answer and stops if they confirm. ${waitNote}`,
            placeholder: 'What changed, how to test it, and what they should see',
            button: 'Send for testing',
            success: 'Sent to the customer for testing.',
            required: true,
        },
        verify: {
            title: 'Resolve as verified by FNN',
            label: 'Use this when you confirmed the fix yourself, for example the data feed is delivering again or the sensor is back online. Your message is posted in the conversation and emailed to the customer, and the ticket closes.',
            placeholder: 'What you checked and what you saw, e.g. “The feed has delivered every batch on time since 14:05.”',
            button: 'Resolve as verified',
            success: 'Resolved as verified by FNN. The customer has been told.',
            required: true,
        },
        resolve: {
            title: 'Resolve and close without validation',
            label: 'Use this when nobody can confirm the fix. These notes are emailed to the customer.',
            placeholder: 'Resolution notes for the customer',
            button: 'Resolve and close',
            success: 'Ticket resolved and closed.',
            required: false,
        },
    }

    if (ticket.status === 'Resolved') {
        return (
            <section className="cs-card">
                <div className="cs-resolution-head">
                    <h3>Resolution</h3>
                    {ticket.resolution_type_label && (
                        <span className={`cs-res-tag ${ticket.is_auto_closed ? 'is-auto' : ''}`}>{ticket.resolution_type_label}</span>
                    )}
                </div>
                <p className="cs-notes">{ticket.resolution_notes || 'No resolution notes.'}</p>
                <p className="cs-card-hint">
                    Resolved {formatDateTime(ticket.resolved_at)}
                    {ticket.resolved_by_name ? ` by ${ticket.resolved_by_name}` : ' automatically'}.
                    {' '}The customer can reopen it for {reopenDays} days.
                    {!ticket.csat_eligible && ' This kind of closure doesn’t get a satisfaction survey.'}
                </p>
                {ticket.survey ? (
                    <div className={`cs-survey is-${ticket.survey.rating >= 4 ? 'good' : ticket.survey.rating === 3 ? 'neutral' : 'bad'}`}>
                        <strong>Customer rating: {ticket.survey.rating}/5, {ticket.survey.rating_label.toLowerCase()}</strong>
                        {ticket.survey.comment && <p>“{ticket.survey.comment}”</p>}
                    </div>
                ) : ticket.csat_eligible && (
                    <p className="cs-card-hint">The customer hasn’t rated this ticket yet.</p>
                )}
            </section>
        )
    }

    const working = p.can_work
    const awaiting = ticket.awaiting_customer

    return (
        <section className="cs-card">
            <h3>Work on it</h3>

            {awaiting && (
                <div className="cs-awaiting">
                    <div>
                        <strong>{awaiting === 'validation' ? 'Waiting for the customer to test the fix' : 'Waiting for the customer’s reply'}</strong>
                        <span>Since {timeAgo(ticket.sla.paused_since ?? ticket.updated_at)}. The SLA clock is paused.</span>
                        <span>
                            Reminders sent: {ticket.awaiting_reminders_sent} of 2
                            {ticket.next_reminder_at && `, next on ${formatDate(ticket.next_reminder_at)}`}.
                            {ticket.auto_close_at && ` Closes automatically on ${formatDateTime(ticket.auto_close_at)} if there’s no reply.`}
                        </span>
                    </div>
                    {working && (
                        <button className="cs-btn" disabled={busy}
                            onClick={() => act('resume', 'Resumed without a customer reply', 'Clock resumed.')}>
                            <FiPlay size={12} /> Resume without reply
                        </button>
                    )}
                </div>
            )}

            {working && (
                <div className="cs-status-row">
                    <label className="cs-field">
                        <span>Status</span>
                        <select value={status} onChange={e => setStatus(e.target.value as StaffTicket['status'])}>
                            {WORKING_STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                    </label>
                    <button className="cs-btn cs-btn-primary" disabled={busy || status === ticket.status}
                        onClick={() => patch({ status }, `Status changed to ${status}.`)}>
                        Update status
                    </button>
                </div>
            )}

            {!compose && (working || p.can_verify || p.can_resolve) && (
                <div className="cs-action-row">
                    {working && !awaiting && (
                        <>
                            <button className="cs-btn" onClick={() => setCompose('request_input')}>
                                <FiPause size={12} /> Ask the customer
                            </button>
                            <button className="cs-btn" onClick={() => setCompose('request_validation')}>
                                Ask the customer to test
                            </button>
                        </>
                    )}
                    {p.can_verify && (
                        <button className="cs-btn cs-btn-verify" onClick={() => setCompose('verify')}>
                            <FiCheckCircle size={12} /> Verified by FNN…
                        </button>
                    )}
                    {p.can_resolve && (
                        <button className="cs-btn cs-btn-quiet" onClick={() => setCompose('resolve')}>Resolve and close…</button>
                    )}
                </div>
            )}

            {compose && (
                <div className={`cs-compose ${compose === 'verify' ? 'is-verify' : ''}`}>
                    <strong>{composeCopy[compose].title}</strong>
                    <p>{composeCopy[compose].label}</p>
                    <textarea rows={4} value={text} onChange={e => setText(e.target.value)}
                        placeholder={composeCopy[compose].placeholder} autoFocus />
                    <div className="cs-card-actions">
                        <button className="cs-btn" disabled={busy} onClick={() => { setCompose(null); setText('') }}>Cancel</button>
                        <button className="cs-btn cs-btn-primary" disabled={busy || (composeCopy[compose].required && !text.trim())}
                            onClick={() => act(compose, text.trim(), composeCopy[compose].success)}>
                            {busy ? 'Sending…' : composeCopy[compose].button}
                        </button>
                    </div>
                </div>
            )}

            {working && (
                <div className="cs-notes-edit">
                    <label className="cs-field">
                        <span>Resolution notes <em>(shared with the customer when the ticket closes)</em></span>
                        <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
                            placeholder="What was wrong and what fixed it" />
                    </label>
                    <div className="cs-card-actions">
                        <button className="cs-btn" disabled={busy || notes === (ticket.resolution_notes ?? '')}
                            onClick={() => patch({ resolution_notes: notes }, 'Notes saved.')}>
                            Save notes
                        </button>
                    </div>
                </div>
            )}
        </section>
    )
}

// ─── Detail: original request (collapsible) ───────────────────────────────────

function RequestSummary({ ticket }: { ticket: StaffTicket }) {
    const [expanded, setExpanded] = useState(false)
    const [overflowing, setOverflowing] = useState(false)
    const textRef = useRef<HTMLParagraphElement>(null)

    // Only offer "Show full request" when the text is actually clamped
    useEffect(() => {
        const el = textRef.current
        if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1)
    }, [ticket.description])

    return (
        <section className="cs-request">
            <div className="cs-request-head">
                <h3>Original request</h3>
                {(overflowing || expanded) && (
                    <button className="cs-link" onClick={() => setExpanded(v => !v)}>
                        {expanded ? 'Show less' : 'Show full request'}
                    </button>
                )}
            </div>
            <p ref={textRef} className={`cs-request-text ${expanded ? 'is-expanded' : ''}`}>{ticket.description}</p>
            {ticket.attachments.length > 0 && (
                <ul className="cs-request-files">
                    {ticket.attachments.map(a => (
                        <li key={a.attachment_id}>
                            <a href={a.file_url} target="_blank" rel="noopener noreferrer">
                                <FiPaperclip size={12} /> {a.file_name}
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function TicketDetail({ ticketId, departments, fnnUsers, now, waitDays, reopenDays, onChanged, onClose }: {
    ticketId: number
    departments: DepartmentOption[]
    fnnUsers: FnnUser[]
    now: number
    waitDays: number
    reopenDays: number
    onChanged: (t: StaffTicket) => void
    onClose: () => void
}) {
    const [ticket, setTicket] = useState<StaffTicket | null>(null)
    const [fetchedAt, setFetchedAt] = useState(Date.now())
    const [error, setError] = useState<string | null>(null)
    const [chatKey, setChatKey] = useState(0)

    const load = useCallback(async () => {
        const { ok, data } = await apiRequest<StaffTicket>(`${BASE}/manage/${ticketId}/`)
        if (ok) {
            setTicket(data)
            setFetchedAt(Date.now())
            setError(null)
            onChanged(data)
        } else {
            setError(errorMessage(data, 'This ticket couldn’t be loaded.'))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId])

    useEffect(() => { setTicket(null); load() }, [load])

    const saved = (t: StaffTicket) => {
        setTicket(t)
        setFetchedAt(Date.now())
        onChanged(t)
        setChatKey(k => k + 1)
    }

    // Someone else changed the ticket (assignee, team, survey…) → reload it
    const onMeta = (meta: ChatMeta) => {
        if (ticket && ticketChanged(meta, ticket.updated_at)) load()
    }

    if (error) return <div className="cs-detail cs-detail-empty"><p>{error}</p><button className="cs-btn" onClick={load}>Try again</button></div>
    if (!ticket) return <div className="cs-detail cs-detail-empty"><p>Loading ticket…</p></div>

    const p = ticket.permissions

    return (
        <div className="cs-detail">
            <header className="cs-detail-head">
                <div className="cs-detail-titlebar">
                    <button className="cs-back" onClick={onClose}><FiArrowLeft size={14} /> Back to list</button>
                    <div className="cs-detail-title">
                        <span className="cs-detail-id">Client ticket #{ticket.ticket_id}</span>
                        <h2>{ticket.title}</h2>
                        <div className="cs-detail-meta">
                            <span className={`cs-status ${STATUS_CLASS[ticket.status]}`}>{ticket.status}</span>
                            <PriorityBadge ticket={ticket} />
                            <span className="cs-tag">{ticket.ticket_type}</span>
                            <span className="cs-tag">{ticket.environment}</span>
                            {ticket.resolution_type_label && (
                                <span className={`cs-res-tag ${ticket.is_auto_closed ? 'is-auto' : ''}`}>{ticket.resolution_type_label}</span>
                            )}
                        </div>
                    </div>
                    <button className="cs-icon-btn" aria-label="Close ticket" onClick={onClose}><FiX /></button>
                </div>

                <div className="cs-summary">
                    <dl className="cs-facts">
                        <div><dt>Customer</dt><dd>{ticket.created_by_name}</dd></div>
                        <div><dt>Organization</dt><dd>{ticket.organization_name ?? '—'}</dd></div>
                        <div><dt>Email</dt><dd><a href={`mailto:${ticket.created_by_email}`}>{ticket.created_by_email}</a></dd></div>
                        <div><dt>Opened</dt><dd>{formatDateTime(ticket.created_at)}</dd></div>
                        <div><dt>Team</dt><dd>{ticket.target_department_name ?? 'Not routed yet'}</dd></div>
                        <div><dt>Assignee</dt><dd>{ticket.assigned_to_name ?? 'Unassigned'}</dd></div>
                    </dl>

                    <SlaCard ticket={ticket} fetchedAt={fetchedAt} now={now} />
                </div>

                <RequestSummary ticket={ticket} />
            </header>

            <div className="cs-detail-grid">
                <div className="cs-detail-side">
                    {p.can_triage && <TriageCard ticket={ticket} departments={departments} onSaved={saved} />}
                    {p.can_assign && <AssignCard ticket={ticket} fnnUsers={fnnUsers} onSaved={saved} />}
                    {(p.can_work || p.can_resolve || ticket.status === 'Resolved') && (
                        <WorkCard ticket={ticket} waitDays={waitDays} reopenDays={reopenDays} onSaved={saved} />
                    )}

                    {!p.can_triage && !p.can_assign && !p.can_work && ticket.status !== 'Resolved' && (
                        <p className="cs-readonly-note">You can follow this ticket. The receiver, team leader and assignee make changes.</p>
                    )}
                </div>

                <section className="cs-chat-card">
                    <div className="cs-chat-head">
                        <h3>Conversation with the customer</h3>
                        <span>Visible to {ticket.created_by_name}</span>
                    </div>
                    <ClientTicketChat ticketId={ticket.ticket_id} perspective="staff" onMeta={onMeta} refreshKey={chatKey}
                        placeholder="Reply to the customer…" />
                </section>
            </div>
        </div>
    )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ClientSupportManagement = () => {
    const [meta, setMeta] = useState<StaffMeta | null>(null)
    const [metaError, setMetaError] = useState<string | null>(null)
    const [view, setView] = useState<View | null>(null)
    const [tickets, setTickets] = useState<StaffTicket[]>([])
    const [fetchedAt, setFetchedAt] = useState(Date.now())
    const [loading, setLoading] = useState(false)
    const [statusFilter, setStatusFilter] = useState<'open' | 'waiting' | 'resolved' | 'all'>('open')
    const [search, setSearch] = useState('')
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [departments, setDepartments] = useState<DepartmentOption[]>([])
    const [fnnUsers, setFnnUsers] = useState<FnnUser[]>([])
    const [showNotif, setShowNotif] = useState(false)
    const now = useNow(30000)

    useEffect(() => {
        apiRequest<StaffMeta>(`${BASE}/manage/meta/`).then(({ ok, data }) => {
            if (!ok) { setMetaError(errorMessage(data, 'You don’t have access to client tickets.')); return }
            setMeta(data)
            setView(data.is_manager || data.is_receiver ? 'inbox' : 'assigned')
        })
        apiRequest<DepartmentOption[]>('/portal/api/support/departments/').then(({ ok, data }) => { if (ok) setDepartments(data) })
        apiRequest<FnnUser[]>('/api/inventory/fnn-users/').then(({ ok, data }) => { if (ok) setFnnUsers(data) })
    }, [])

    const loadList = useCallback(async (v: View, quiet = false) => {
        if (!quiet) setLoading(true)
        const { ok, data } = await apiRequest<StaffTicket[]>(`${BASE}/manage/?view=${v}`)
        if (ok) {
            setTickets(data)
            setFetchedAt(Date.now())
        } else if (!quiet) {
            toast.error(errorMessage(data, 'Tickets couldn’t be loaded.'))
        }
        setLoading(false)
    }, [])

    useEffect(() => {
        if (!view) return
        loadList(view)
        const timer = window.setInterval(() => { if (!document.hidden) loadList(view, true) }, LIST_POLL_MS)
        return () => window.clearInterval(timer)
    }, [view, loadList])

    const tabs = useMemo(() => {
        if (!meta) return []
        const list: { key: View; label: string }[] = []
        if (meta.is_manager || meta.is_receiver) list.push({ key: 'inbox', label: 'Needs triage' })
        list.push({ key: 'assigned', label: 'Assigned to me' })
        if (meta.led_departments.length || meta.my_department) list.push({ key: 'department', label: 'My team' })
        if (meta.is_manager || meta.is_receiver) list.push({ key: 'all', label: 'All tickets' })
        return list
    }, [meta])

    const visible = useMemo(() => {
        const q = search.toLowerCase().trim()
        return tickets.filter(t => {
            if (statusFilter === 'open' && t.status === 'Resolved') return false
            if (statusFilter === 'resolved' && t.status !== 'Resolved') return false
            if (statusFilter === 'waiting' && !t.awaiting_customer) return false
            if (!q) return true
            return [t.title, t.organization_name, t.created_by_name, String(t.ticket_id)]
                .some(v => (v ?? '').toLowerCase().includes(q))
        })
    }, [tickets, statusFilter, search])

    const onChanged = useCallback((t: StaffTicket) => {
        setTickets(prev => prev.map(p => (p.ticket_id === t.ticket_id ? { ...t, unread_count: 0 } : p)))
    }, [])

    const manageable = meta
        ? (meta.is_manager ? departments : departments.filter(d => meta.led_departments.includes(d.name)))
        : []

    if (metaError) {
        return (
            <div>
                <TeamHeader title="Client Tickets" />
                <div className="customer-directory-body cs-root cs-page"><div className="cs-blocked"><h2>Client tickets</h2><p>{metaError}</p></div></div>
            </div>
        )
    }

    return (
        <div>
            <ToastContainer position="bottom-right" />
            <TeamHeader title="Client Tickets" />

            <div className="customer-directory-body cs-root">
                <div className="cs-top">
                    <div>
                        <h1>Client tickets</h1>
                        <p>
                            New tickets go to {meta?.receiver_name ?? 'the ticket receiver'} for triage, then to the team that resolves them.
                        </p>
                    </div>
                    {manageable.length > 0 && (
                        <button className="cs-btn" onClick={() => setShowNotif(true)}>
                            <FiBell size={13} /> Notifications
                        </button>
                    )}
                </div>

                <div className={`cs-layout ${selectedId ? 'show-detail' : ''}`}>
                    <aside className="cs-list">
                        <div className="cs-tabs" role="tablist">
                            {tabs.map(t => (
                                <button key={t.key} role="tab" aria-selected={view === t.key}
                                    className={view === t.key ? 'active' : ''}
                                    onClick={() => { setView(t.key); setSelectedId(null) }}>
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        <div className="cs-filters">
                            <div className="cs-search">
                                <FiSearch size={14} />
                                <input value={search} onChange={e => setSearch(e.target.value)}
                                    placeholder="Search title, customer or #" aria-label="Search tickets" />
                            </div>
                            <div className="cs-filter-chips">
                                {(['open', 'waiting', 'resolved', 'all'] as const).map(f => (
                                    <button key={f} className={statusFilter === f ? 'active' : ''} onClick={() => setStatusFilter(f)}>
                                        {f === 'open' ? 'Open' : f === 'waiting' ? 'Waiting on customer' : f === 'resolved' ? 'Resolved' : 'All'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="cs-items">
                            {loading && <p className="cs-empty">Loading tickets…</p>}
                            {!loading && visible.length === 0 && (
                                <p className="cs-empty">
                                    {view === 'inbox' ? 'Nothing waiting for triage.' : 'No tickets match these filters.'}
                                </p>
                            )}

                            {!loading && visible.map(t => (
                                <button key={t.ticket_id}
                                    className={`cs-item ${selectedId === t.ticket_id ? 'active' : ''}`}
                                    onClick={() => setSelectedId(t.ticket_id)}>
                                    <span className="cs-item-top">
                                        <span className="cs-item-id">#{t.ticket_id}</span>
                                        <PriorityBadge ticket={t} />
                                        <span className={`cs-status ${STATUS_CLASS[t.status]}`}>{t.status}</span>
                                        {t.is_auto_closed && <span className="cs-res-tag is-auto">Auto-closed</span>}
                                        {t.unread_count > 0 && <span className="cs-unread" aria-label={`${t.unread_count} unread`}>{t.unread_count}</span>}
                                    </span>
                                    <span className="cs-item-title">{t.title}</span>
                                    <span className="cs-item-sub">
                                        {t.organization_name ?? t.created_by_name}
                                        {t.assigned_to_name ? `, ${t.assigned_to_name}` : t.target_department_name ? `, ${t.target_department_name} (unassigned)` : ''}
                                    </span>
                                    <span className="cs-item-sla">
                                        <SlaChip sla={t.sla} fetchedAt={fetchedAt} now={now} />
                                        <span className="cs-muted">
                                            {t.auto_close_at ? `Auto-closes ${formatDate(t.auto_close_at)}` : timeAgo(t.updated_at)}
                                        </span>
                                    </span>
                                    <SlaMeter sla={t.sla} fetchedAt={fetchedAt} now={now} />
                                </button>
                            ))}
                        </div>
                    </aside>

                    <main className="cs-main">
                        {selectedId ? (
                            <TicketDetail
                                key={selectedId}
                                ticketId={selectedId}
                                departments={departments}
                                fnnUsers={fnnUsers}
                                now={now}
                                waitDays={meta?.awaiting_auto_close_business_days ?? 10}
                                reopenDays={meta?.reopen_window_days ?? 14}
                                onChanged={onChanged}
                                onClose={() => setSelectedId(null)}
                            />
                        ) : (
                            <div className="cs-placeholder">
                                <h2>Select a ticket</h2>
                                <p>The list is sorted newest first. The bar under each ticket shows how much of its SLA time has been used. The SLA clock only counts business days.</p>
                                <ul className="cs-legend">
                                    <li><span className="cs-sla-chip is-ok">On track</span></li>
                                    <li><span className="cs-sla-chip is-soon">Under 25% left</span></li>
                                    <li><span className="cs-sla-chip is-paused"><FiPause size={10} /> Waiting on customer</span></li>
                                    <li><span className="cs-sla-chip is-late">Overdue</span></li>
                                </ul>
                                {meta && (
                                    <p className="cs-muted">
                                        SLA targets (business hours): {Object.entries(meta.sla_target_hours).map(([k, v]) => `${k} ${v}h`).join(', ')}.
                                        {' '}Tickets waiting on the customer close after {meta.awaiting_auto_close_business_days} business days,
                                        with reminders on days {meta.awaiting_reminder_business_days.join(' and ')}.
                                    </p>
                                )}
                            </div>
                        )}
                    </main>
                </div>
            </div>

            {showNotif && (
                <NotificationSettingsModal kind="client" departments={manageable} onClose={() => setShowNotif(false)} />
            )}
        </div>
    )
}

export default ClientSupportManagement